import { supabase } from './supabase';
import { createLogger } from './logger';
import { FREQUENCY_CONFIG } from '../components/NotificationSettings/frequencyConfig';

// Fixed: Added safety checks for userSettings.delivery_methods
const logger = createLogger({ module: 'notificationService' });

export interface NotificationTemplate {
  id: string;
  key: string;
  title_template: string;
  message_template: string;
  notification_type: 'info' | 'success' | 'warning' | 'error';
  category: string;
  priority: number;
  delivery_method: 'banner' | 'bell' | 'email' | 'push';
  action_url_template?: string;
  action_text_template?: string;
  metadata_template?: Record<string, any>;
  template_category: string;
  target_page?: string;
  frequency_limit?: Record<string, any>;
  retention_days: number;
  is_critical: boolean;
  active: boolean;
  ui_control_key?: string; // Phase 3: Template-to-control mapping
  default_frequency?: string; // Phase 3: Template default frequency
}

export interface UserNotification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  notification_type: 'info' | 'success' | 'warning' | 'error';
  category: string;
  priority: number;
  delivery_method: 'banner' | 'bell' | 'email' | 'push';
  action_url?: string;
  action_text?: string;
  metadata?: Record<string, any>;
  read_at?: string;
  dismissed_at?: string;
  delivered_at?: string;
  expiration_date?: string;
  created_at: string;
  template?: NotificationTemplate;
}

export interface NotificationServiceOptions {
  userId: string;
  includeRead?: boolean;
  includeDismissed?: boolean;
  deliveryMethod?: 'banner' | 'bell' | 'email' | 'push';
  category?: string;
  limit?: number;
}

// User notification settings interface (matches database schema)
export interface UserNotificationSettings {
  scheduled_notifications: {
    notification_types: Record<string, boolean>;
    notification_frequencies: Record<string, string>;
  };
  delivery_methods: {
    bell_enabled: boolean;
    banner_enabled: boolean;
    email_enabled: boolean;
  };
}

// Database notification settings record interface
export interface NotificationSettingsRecord {
  id: string;
  user_id: string;
  settings: UserNotificationSettings;
  created_at: string;
  updated_at: string;
}

class NotificationService {
  private static instance: NotificationService;

  /**
   * Phase 3: Get effective frequency based on UI control state
   */
  private getEffectiveFrequency(
    template: NotificationTemplate,
    userSettings: UserNotificationSettings
  ): string {
    // Safety check: ensure userSettings and scheduled_notifications exist
    if (!userSettings || !userSettings.scheduled_notifications) {
      logger.warn('UserSettings or scheduled_notifications undefined, using template default');
      return template.default_frequency || 'weekly';
    }

    // Critical templates use their default frequency
    if (template.category === 'critical' || template.is_critical) {
      return template.default_frequency || 'immediate';
    }

    const uiControlKey = template.ui_control_key;
    if (!uiControlKey) {
      // Fallback for unmapped templates
      return template.default_frequency || 'weekly';
    }

    const isEnabled = userSettings.scheduled_notifications.notification_types[uiControlKey];
    
    if (isEnabled) {
      // Use user's chosen frequency
      return userSettings.scheduled_notifications.notification_frequencies[uiControlKey];
    } else {
      // Use lowest frequency from available options
      const frequencyConfig = FREQUENCY_CONFIG[uiControlKey];
      if (frequencyConfig?.options) {
        return this.getLowestFrequency(frequencyConfig.options);
      }
      return 'monthly'; // Fallback lowest frequency
    }
  }

  /**
   * Phase 3: Get effective delivery methods based on UI control state
   * NOTE: This is used for metadata tracking only. The actual delivery method
   * comes from the template's delivery_method field.
   */
  private getEffectiveDeliveryMethods(
    template: NotificationTemplate,
    userSettings: UserNotificationSettings
  ): string[] {
    // Safety check: ensure userSettings and delivery_methods exist
    if (!userSettings || !userSettings.delivery_methods) {
      logger.warn('UserSettings or delivery_methods undefined, using defaults', { userSettings });
      return ['bell', 'banner']; // Default delivery methods
    }

    // Critical templates get all delivery methods
    if (template.category === 'critical' || template.is_critical) {
      const methods = ['bell', 'banner'];
      if (userSettings.delivery_methods.email_enabled) {
        methods.push('email');
      }
      return methods;
    }

    const uiControlKey = template.ui_control_key;
    if (!uiControlKey) {
      // Fallback for unmapped templates - use bell as default
      return ['bell'];
    }

    const isEnabled = userSettings.scheduled_notifications?.notification_types?.[uiControlKey];
    
    if (isEnabled) {
      // Use user's enabled delivery methods
      // Note: banner_enabled is controlled by bell_enabled (in-app notifications)
      const methods: string[] = [];
      if (userSettings.delivery_methods.bell_enabled) {
        methods.push('bell');
        methods.push('banner'); // If in-app is enabled, both bell and banner are allowed
      }
      if (userSettings.delivery_methods.email_enabled) methods.push('email');
      return methods.length > 0 ? methods : ['bell'];
    } else {
      // Disabled notifications still get 'bell' delivery method but won't show in active tab
      return ['bell'];
    }
  }

  /**
   * Phase 3: Get lowest frequency from options
   */
  private getLowestFrequency(options: Array<{ value: string; label: string }>): string {
    // Define frequency hierarchy (lowest to highest)
    const frequencyOrder = [
      'annually', 'semi_annually', 'quarterly', 'monthly', 
      'biweekly', 'weekly', 'weekly_end', 'weekly_start', 
      'daily', 'immediate'
    ];

    for (const freq of frequencyOrder) {
      if (options.some(opt => opt.value === freq)) {
        return freq;
      }
    }
    
    return options[options.length - 1]?.value || 'monthly';
  }

  /**
   * Optimized tab filtering with database-level filtering
   */
  /**
   * Simplified tab filtering - no longer needed since filtering happens at creation time
   * Keep for backwards compatibility but now just returns all notifications
   */
  async filterNotificationsForTab(
    notifications: UserNotification[],
    tab: 'new' | 'all',
    userId: string
  ): Promise<UserNotification[]> {
    // Since user preferences are now applied at notification creation time,
    // all notifications in the active table should be displayed
    // History tab should query notification_history table separately
    return notifications;
  }

  /**
   * Get user notification settings (simplified after schema standardization)
   */
  private async getUserNotificationSettings(userId: string): Promise<UserNotificationSettings> {
    try {
      const { data, error } = await supabase
        .from('notification_settings')
        .select('settings')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        logger.error('Error fetching user notification settings:', error);
      }

      // Default settings if none exist
      const defaultSettings: UserNotificationSettings = {
        scheduled_notifications: {
          notification_types: {
            documentation_reminders: true,
            progress_summaries: true,
            achievement_celebrations: true,
            goal_setting_prompts: true,
            reflection_reminders: true,
            self_review: true,
            career_opportunities: true,
            milestone_celebrations: true,
            streak_reminders: true
          },
          notification_frequencies: {
            documentation_reminders: 'weekly_end',
            progress_summaries: 'weekly',
            achievement_celebrations: 'immediate',
            goal_setting_prompts: 'monthly',
            reflection_reminders: 'monthly',
            self_review: 'quarterly',
            career_opportunities: 'quarterly',
            milestone_celebrations: 'immediate',
            streak_reminders: 'weekly'
          }
        },
        delivery_methods: {
          bell_enabled: true,
          banner_enabled: true, // This is now controlled by bell_enabled
          email_enabled: true
        }
      };

      // Database now stores settings in standardized format - no conversion needed
      if (data?.settings) {
        return {
          scheduled_notifications: data.settings.scheduled_notifications || defaultSettings.scheduled_notifications,
          delivery_methods: data.settings.delivery_methods || defaultSettings.delivery_methods
        };
      }

      return defaultSettings;
    } catch (error) {
      logger.error('Error in getUserNotificationSettings:', error);
      return this.getDefaultSettings();
    }
  }

  /**
   * Get default notification settings
   */
  private getDefaultSettings(): UserNotificationSettings {
    return {
      scheduled_notifications: {
        notification_types: {
          documentation_reminders: true,
          progress_summaries: true,
          achievement_celebrations: true,
          goal_setting_prompts: true,
          reflection_reminders: true,
          self_review: true,
          career_opportunities: true,
          milestone_celebrations: true,
          streak_reminders: true
        },
        notification_frequencies: {
          documentation_reminders: 'weekly_end',
          progress_summaries: 'weekly',
          achievement_celebrations: 'immediate',
          goal_setting_prompts: 'monthly',
          reflection_reminders: 'monthly',
          self_review: 'quarterly',
          career_opportunities: 'quarterly',
          milestone_celebrations: 'immediate',
          streak_reminders: 'weekly'
        }
      },
      delivery_methods: {
        bell_enabled: true,
        banner_enabled: true,
        email_enabled: true
      }
    };
  }

  /**
   * Optimized fetch for active notifications
   */
  async getUserNotifications(options: NotificationServiceOptions): Promise<UserNotification[]> {
    const {
      userId,
      includeRead = false,
      includeDismissed = false,
      deliveryMethod,
      category,
      limit = 50
    } = options;

    // Build optimized query using method chaining for better index usage
    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId);

    // Apply filters in order of selectivity for better performance
    if (!includeRead) query = query.is('read_at', null);
    if (!includeDismissed) query = query.is('dismissed_at', null);
    if (deliveryMethod) query = query.eq('delivery_method', deliveryMethod);
    if (category) query = query.eq('category', category);
    
    // Filter out expired notifications
    query = query.or('expiration_date.is.null,expiration_date.gt.' + new Date().toISOString());

    const { data, error } = await query
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      logger.error('Error fetching notifications:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get banner notifications specifically
   */
  async getBannerNotifications(userId: string): Promise<UserNotification[]> {
    return this.getUserNotifications({
      userId,
      deliveryMethod: 'banner',
      includeRead: true,
      includeDismissed: false,
      limit: 5
    });
  }

  /**
   * Get notification history from the history table
   */
  async getNotificationHistory(options: NotificationServiceOptions): Promise<UserNotification[]> {
    const {
      userId,
      deliveryMethod,
      category,
      limit = 50
    } = options;

    // Build query for history table
    let query = supabase
      .from('notification_history')
      .select('*')
      .eq('user_id', userId);

    // Apply filters
    if (deliveryMethod) query = query.eq('delivery_method', deliveryMethod);
    if (category) query = query.eq('category', category);

    const { data, error } = await query
      .order('archived_at', { ascending: false })
      .limit(limit);

    if (error) {
      logger.error('Error fetching notification history:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Archive old read notifications to history table
   */
  async archiveOldReadNotifications(userId: string, daysOld: number = 30): Promise<number> {
    try {
      // Use the database function to archive old read notifications
      const { data, error } = await supabase
        .rpc('auto_archive_old_read_notifications', {
          p_days_old: daysOld
        });

      if (error) {
        logger.error('Error archiving old read notifications:', error);
        return 0;
      }

      const archivedCount = data as number;
      logger.info(`Archived ${archivedCount} old read notifications for user ${userId}`);
      return archivedCount;
    } catch (error) {
      logger.error('Error in archiveOldReadNotifications:', error);
      return 0;
    }
  }

  /**
   * Create a notification for a user from a template
   * Phase 3: Enhanced with frequency and delivery method resolution
   */
  async createNotificationFromTemplate(
    userId: string,
    templateKey: string,
    variables: Record<string, any> = {}
  ): Promise<UserNotification | null> {
    try {
      // First, get the template
      const { data: template, error: templateError } = await supabase
        .from('notification_templates')
        .select('*')
        .eq('key', templateKey)
        .eq('active', true)
        .single();

      if (templateError || !template) {
        logger.error('Template not found or inactive:', { templateKey, error: templateError });
        return null;
      }

      // Phase 3: Get user settings and resolve effective frequency/delivery
      const userSettings = await this.getUserNotificationSettings(userId);
      const effectiveFrequency = this.getEffectiveFrequency(template, userSettings);
      const effectiveDeliveryMethods = this.getEffectiveDeliveryMethods(template, userSettings);

      // Check frequency limits (enhanced with effective frequency)
      const canCreate = await this.checkFrequencyLimits(userId, template, effectiveFrequency);
      if (!canCreate) {
        logger.debug('Notification blocked by frequency limits:', { templateKey, userId, effectiveFrequency });
        return null;
      }

      // Validate template before processing
      const validationResult = this.validateTemplate(template, variables);
      if (!validationResult.isValid) {
        logger.warn('Template validation failed', {
          templateKey,
          issues: validationResult.issues,
          variables: Object.keys(variables)
        });
      }

      // Process templates with variables
      const title = this.processTemplate(template.title_template, variables);
      const message = this.processTemplate(template.message_template, variables);
      const actionUrl = template.action_url_template 
        ? this.processTemplate(template.action_url_template, variables)
        : null;
      const actionText = template.action_text_template
        ? this.processTemplate(template.action_text_template, variables)
        : null;

      // Validate processed content
      if (this.hasUnprocessedVariables(message) || this.hasUnprocessedVariables(title)) {
        logger.error('Template processing left unprocessed variables', {
          templateKey,
          title,
          message: message.substring(0, 100),
          variables: Object.keys(variables)
        });
      }

      // Calculate expiration date
      const expirationDate = template.retention_days
        ? new Date(Date.now() + template.retention_days * 24 * 60 * 60 * 1000)
        : new Date(Date.now() + 60 * 24 * 60 * 60 * 1000); // Default 60 days

      // Use the template's specified delivery method
      // The template defines where this notification should appear (bell, banner, etc.)
      const primaryDeliveryMethod = template.delivery_method || effectiveDeliveryMethods[0] || 'internal';

      // Extract deduplication key from variables if provided
      const deduplicationKey = variables._deduplication_key as string | undefined;
      const cleanedVariables = { ...variables };
      delete cleanedVariables._deduplication_key;

      // Create the notification
      const { data: notification, error: createError } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          title,
          message,
          notification_type: template.notification_type,
          category: template.category,
          priority: template.priority,
          delivery_method: primaryDeliveryMethod,
          action_url: actionUrl,
          action_text: actionText,
          metadata: {
            ...template.metadata_template,
            ...cleanedVariables,
            template_key: templateKey,
            effective_frequency: effectiveFrequency, // Phase 3: Track effective frequency
            effective_delivery_methods: effectiveDeliveryMethods, // Phase 3: Track all delivery methods
            ui_control_key: template.ui_control_key, // Phase 3: Track UI control mapping
            target_page: template.target_page // Store target page for banner notification indicators
          },
          expiration_date: expirationDate.toISOString(),
          notification_category: template.template_category,
          deduplication_key: deduplicationKey // Add deduplication key to prevent duplicates
        })
        .select()
        .single();

      if (createError) {
        logger.error('Error creating notification:', createError);
        throw createError;
      }

      return notification;
    } catch (error) {
      logger.error('Error in createNotificationFromTemplate:', error);
      return null;
    }
  }

  /**
   * Dismiss a notification - trigger will automatically move it to notification_history table
   */
  async dismissNotification(notificationId: string, userId: string): Promise<boolean> {
    try {
      logger.info('Starting dismissNotification:', { notificationId, userId });
      
      // Set both dismissed_at and read_at - dismissing should also mark as read
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('notifications')
        .update({ 
          dismissed_at: now,
          read_at: now // Mark as read when dismissed
        })
        .eq('id', notificationId)
        .eq('user_id', userId)
        .select(); // Add select to see what was updated

      if (error) {
        logger.error('Supabase error dismissing notification:', { 
          error: error.message, 
          code: error.code,
          details: error.details,
          notificationId, 
          userId 
        });
        return false;
      }

      if (!data || data.length === 0) {
        logger.warn('No notification found to dismiss:', { notificationId, userId });
        // Still return true since the notification might have already been dismissed
        return true;
      }

      logger.info('Dismiss update successful - trigger will move to history:', { 
        updatedCount: data.length, 
        notificationId, 
        userId 
      });
      return true;
    } catch (error) {
      logger.error('Unexpected error in dismissNotification:', { 
        error: error instanceof Error ? error.message : String(error), 
        notificationId, 
        userId 
      });
      return false;
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string, userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', userId);

      if (error) {
        logger.error('Error marking notification as read:', error);
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Error in markAsRead:', error);
      return false;
    }
  }

  /**
   * Mark notification as delivered (when received via realtime)
   */
  async markAsDelivered(notificationId: string, userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ delivered_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', userId);

      if (error) {
        logger.error('Error marking notification as delivered:', error);
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Error in markAsDelivered:', error);
      return false;
    }
  }

  /**
   * Check if user should receive onboarding notification
   */
  async shouldShowOnboardingNotification(userId: string): Promise<boolean> {
    try {
      // Check if user has completed onboarding
      const { data: user } = await supabase
        .from('users')
        .select('onboarding_complete')
        .eq('id', userId)
        .single();

      if (user?.onboarding_complete) {
        return false;
      }

      // Check if they already have an active onboarding notification
      const existingNotifications = await this.getUserNotifications({
        userId,
        category: 'onboarding',
        deliveryMethod: 'banner',
        includeRead: true,
        includeDismissed: false
      });

      return existingNotifications.length === 0;
    } catch (error) {
      logger.error('Error checking onboarding notification status:', error);
      return false;
    }
  }

  /**
   * Enhanced template processing with validation, fallbacks, and error handling
   */
  private processTemplate(template: string, variables: Record<string, any>): string {
    try {
      let processed = template;
      const processedVariables = new Set<string>();
      const missingVariables: string[] = [];
      
      // Define fallbacks for common variables
      const VARIABLE_FALLBACKS: Record<string, string> = {
        first_name: 'there',
        card_count: '0',
        streak_days: '1',
        item_count: '0',
        note_count: '0',
        card_title: 'your card',
        note_title: 'your note',
        review_name: 'your review',
        goal_name: 'your goal',
        achievement_name: 'this achievement',
        suggestion_text: 'a new opportunity',
        current_year: new Date().getFullYear().toString(),
        new_level: '1',
        xp_earned: '0',
        word_count: '0',
        total_items: '0'
      };

      // Sanitize and validate input variables
      const sanitizedVariables = this.sanitizeVariables(variables);
      
      // Find all variables in template
      const variableMatches = template.match(/\{\{([^}]+)\}\}/g) || [];
      const templateVariables = variableMatches.map(match => 
        match.replace(/[{}]/g, '').trim()
      );

      // Process each variable
      templateVariables.forEach(variableName => {
        const placeholder = `{{${variableName}}}`;
        let value: string;

        if (sanitizedVariables.hasOwnProperty(variableName) && sanitizedVariables[variableName] != null) {
          // Use provided value
          value = String(sanitizedVariables[variableName]);
        } else if (VARIABLE_FALLBACKS.hasOwnProperty(variableName)) {
          // Use fallback value
          value = VARIABLE_FALLBACKS[variableName];
          logger.debug(`Using fallback for variable: ${variableName}`, { 
            template: template.substring(0, 50),
            fallback: value 
          });
        } else {
          // Variable not found and no fallback - log error and use placeholder
          missingVariables.push(variableName);
          value = `[${variableName}]`; // Visible indicator of missing variable
          logger.warn(`Missing variable with no fallback: ${variableName}`, {
            template: template.substring(0, 50),
            availableVariables: Object.keys(sanitizedVariables)
          });
        }

        // Replace all instances of this variable
        processed = processed.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
        processedVariables.add(variableName);
      });

      // Log processing results for monitoring
      if (missingVariables.length > 0) {
        logger.error('Template processing completed with missing variables', {
          template: template.substring(0, 100),
          missingVariables,
          processedVariables: Array.from(processedVariables),
          result: processed.substring(0, 100)
        });
      } else {
        logger.debug('Template processing successful', {
          variableCount: processedVariables.size,
          processedVariables: Array.from(processedVariables)
        });
      }

      return processed;
    } catch (error) {
      logger.error('Template processing failed', {
        template: template.substring(0, 100),
        variables: Object.keys(variables),
        error: error instanceof Error ? error.message : String(error)
      });
      
      // Return template with error indicator
      return `${template} [PROCESSING_ERROR]`;
    }
  }

  /**
   * Sanitize and validate template variables
   */
  private sanitizeVariables(variables: Record<string, any>): Record<string, string> {
    const sanitized: Record<string, string> = {};
    
    Object.entries(variables).forEach(([key, value]) => {
      if (value == null) {
        // Skip null/undefined values - will use fallback
        return;
      }

      let sanitizedValue = String(value);
      
      // Trim whitespace
      sanitizedValue = sanitizedValue.trim();
      
      // Remove HTML tags for security
      sanitizedValue = sanitizedValue.replace(/<[^>]*>/g, '');
      
      // Apply length limits based on variable type
      const maxLengths: Record<string, number> = {
        first_name: 50,
        card_title: 100,
        note_title: 100,
        review_name: 100,
        suggestion_text: 200,
        achievement_name: 100,
        goal_name: 100
      };
      
      const maxLength = maxLengths[key] || 150; // Default max length
      if (sanitizedValue.length > maxLength) {
        sanitizedValue = sanitizedValue.substring(0, maxLength - 3) + '...';
        logger.debug(`Truncated variable ${key} to ${maxLength} characters`);
      }
      
      // Limit word count for readability (max 20 words for text fields)
      if (['suggestion_text', 'achievement_name', 'goal_name'].includes(key)) {
        const words = sanitizedValue.split(/\s+/);
        if (words.length > 20) {
          sanitizedValue = words.slice(0, 20).join(' ') + '...';
          logger.debug(`Truncated variable ${key} to 20 words`);
        }
      }
      
      sanitized[key] = sanitizedValue;
    });
    
    return sanitized;
  }

  /**
   * Validate template structure and variable usage
   */
  private validateTemplate(template: NotificationTemplate, variables: Record<string, any>): {
    isValid: boolean;
    issues: string[];
  } {
    const issues: string[] = [];
    
    // Check variable count limits
    const titleVariables = (template.title_template.match(/\{\{([^}]+)\}\}/g) || []).length;
    const messageVariables = (template.message_template.match(/\{\{([^}]+)\}\}/g) || []).length;
    const totalVariables = titleVariables + messageVariables;
    
    if (totalVariables > 3) {
      issues.push(`Too many variables: ${totalVariables} (max 3 recommended)`);
    }
    
    // Check for deprecated variables that should be removed
    const deprecatedVariables = ['registration_method', 'completion_percentage', 'days_inactive', 'days_since_update', 'months_active'];
    const templateText = `${template.title_template} ${template.message_template}`;
    
    deprecatedVariables.forEach(deprecated => {
      if (templateText.includes(`{{${deprecated}}}`)) {
        issues.push(`Contains deprecated variable: {{${deprecated}}}`);
      }
    });
    
    // Check for overly complex templates
    const complexTemplates = ['achievement_unlocked', 'level_up', 'review_completed', 'streak_milestone'];
    if (complexTemplates.includes(template.key) && totalVariables > 3) {
      issues.push(`Complex template should be simplified: ${template.key}`);
    }
    
    // Check first-action templates for educational content
    const firstActionTemplates = ['first_card_created', 'first_note_created', 'first_file_upload'];
    if (firstActionTemplates.includes(template.key)) {
      const hasEducationalContent = template.message_template.length > 100; // Rough check for educational content
      if (!hasEducationalContent) {
        issues.push(`First-action template should include educational content: ${template.key}`);
      }
    }
    
    return {
      isValid: issues.length === 0,
      issues
    };
  }

  /**
   * Check if text contains unprocessed template variables
   */
  private hasUnprocessedVariables(text: string): boolean {
    return /\{\{[^}]+\}\}/.test(text);
  }

  /**
   * Optimized frequency limits check with single query
   */
  private async checkFrequencyLimits(
    userId: string, 
    template: NotificationTemplate, 
    effectiveFrequency?: string
  ): Promise<boolean> {
    try {
      if (!template.frequency_limit) {
        return true;
      }

      const limits = template.frequency_limit;
      const maxPerDay = limits.max_per_day || 999;
      const cooldownHours = limits.cooldown_hours || 0;
      const deduplicationWindow = limits.deduplication_window || 0;

      // Adjust limits based on effective frequency
      let adjustedMaxPerDay = maxPerDay;
      let adjustedCooldownHours = cooldownHours;

      if (effectiveFrequency) {
        if (effectiveFrequency === 'annually') {
          adjustedMaxPerDay = Math.min(maxPerDay, 1);
          adjustedCooldownHours = Math.max(cooldownHours, 24 * 30);
        } else if (effectiveFrequency === 'quarterly') {
          adjustedMaxPerDay = Math.min(maxPerDay, 1);
          adjustedCooldownHours = Math.max(cooldownHours, 24 * 7);
        } else if (effectiveFrequency === 'monthly') {
          adjustedMaxPerDay = Math.min(maxPerDay, 2);
          adjustedCooldownHours = Math.max(cooldownHours, 24);
        }
      }

      // Single optimized query to check all limits at once
      const now = new Date();
      const dayStart = new Date(now);
      dayStart.setHours(0, 0, 0, 0);
      
      const checkStart = new Date(Math.min(
        dayStart.getTime(),
        now.getTime() - adjustedCooldownHours * 60 * 60 * 1000,
        now.getTime() - deduplicationWindow * 1000
      ));

      const { data: recentNotifications } = await supabase
        .from('notifications')
        .select('created_at')
        .eq('user_id', userId)
        .eq('metadata->>template_key', template.key)
        .gte('created_at', checkStart.toISOString())
        .order('created_at', { ascending: false });

      if (!recentNotifications) return true;

      // Check daily limit
      const dailyCount = recentNotifications.filter(n => 
        new Date(n.created_at) >= dayStart
      ).length;
      
      if (dailyCount >= adjustedMaxPerDay) return false;

      // Check cooldown period
      if (adjustedCooldownHours > 0) {
        const cooldownStart = new Date(now.getTime() - adjustedCooldownHours * 60 * 60 * 1000);
        const cooldownCount = recentNotifications.filter(n => 
          new Date(n.created_at) >= cooldownStart
        ).length;
        
        if (cooldownCount > 0) return false;
      }

      // Check deduplication window
      if (deduplicationWindow > 0) {
        const dedupeStart = new Date(now.getTime() - deduplicationWindow * 1000);
        const dedupeCount = recentNotifications.filter(n => 
          new Date(n.created_at) >= dedupeStart
        ).length;
        
        if (dedupeCount > 0) return false;
      }

      return true;
    } catch (error) {
      logger.error('Error checking frequency limits:', error);
      return false;
    }
  }

  /**
   * Run automatic cleanup of old notifications
   * Moves notifications to history based on age and read status rules
   */
  async runAutomaticCleanup(): Promise<{
    age_based_cleanup: number;
    overflow_cleanup: number;
    total_moved: number;
    timestamp: string;
  }> {
    try {
      const { data, error } = await supabase.rpc('run_notification_cleanup');
      
      if (error) {
        logger.error('Error running automatic cleanup:', error);
        throw error;
      }
      
      logger.info('Automatic cleanup completed:', data);
      return data;
    } catch (error) {
      logger.error('Failed to run automatic cleanup:', error);
      throw error;
    }
  }

  /**
   * Get cleanup configuration
   */
  async getCleanupConfig(): Promise<{
    age_cleanup_days: number;
    read_cleanup_hours: number;
    max_active_notifications: number;
    critical_categories: string[];
  }> {
    try {
      const { data, error } = await supabase
        .from('notification_cleanup_config')
        .select('config_key, config_value');
      
      if (error) {
        logger.error('Error fetching cleanup config:', error);
        throw error;
      }
      
      // Convert array to object format
      const config = data.reduce((acc, item) => {
        acc[item.config_key] = item.config_value;
        return acc;
      }, {} as Record<string, any>);
      
      return {
        age_cleanup_days: parseInt(config.age_cleanup_days),
        read_cleanup_hours: parseInt(config.read_cleanup_hours),
        max_active_notifications: parseInt(config.max_active_notifications),
        critical_categories: config.critical_categories
      };
    } catch (error) {
      logger.error('Failed to get cleanup config:', error);
      throw error;
    }
  }

  /**
   * Update cleanup configuration (admin only)
   */
  async updateCleanupConfig(updates: {
    age_cleanup_days?: number;
    read_cleanup_hours?: number;
    max_active_notifications?: number;
    critical_categories?: string[];
  }): Promise<void> {
    try {
      const updatePromises = Object.entries(updates).map(([key, value]) => {
        return supabase
          .from('notification_cleanup_config')
          .update({ 
            config_value: typeof value === 'object' ? value : value.toString(),
            updated_at: new Date().toISOString()
          })
          .eq('config_key', key);
      });
      
      const results = await Promise.all(updatePromises);
      
      for (const result of results) {
        if (result.error) {
          logger.error('Error updating cleanup config:', result.error);
          throw result.error;
        }
      }
      
      logger.info('Cleanup configuration updated:', updates);
    } catch (error) {
      logger.error('Failed to update cleanup config:', error);
      throw error;
    }
  }

  /**
   * Get notification statistics including cleanup metrics
   */
  async getNotificationStats(userId: string): Promise<{
    active_count: number;
    history_count: number;
    unread_count: number;
    auto_cleaned_count: number;
    manually_dismissed_count: number;
    recent_cleanup_activity: Array<{
      cleanup_reason: string;
      count: number;
      last_cleanup: string;
    }>;
  }> {
    try {
      const [activeData, historyData, cleanupData] = await Promise.all([
        // Count active notifications
        supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId),
        
        // Count history notifications
        supabase
          .from('notification_history')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId),
        
        // Get cleanup activity from last 30 days
        supabase
          .from('notification_history')
          .select('cleanup_metadata')
          .eq('user_id', userId)
          .gte('moved_to_history_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
          .not('cleanup_metadata', 'is', null)
      ]);

      if (activeData.error) throw activeData.error;
      if (historyData.error) throw historyData.error;
      if (cleanupData.error) throw cleanupData.error;

      // Count unread notifications
      const { data: unreadData, error: unreadError } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .is('read_at', null);
      
      if (unreadError) throw unreadError;

      // Process cleanup activity
      const cleanupActivity = cleanupData.data?.reduce((acc, item) => {
        if (item.cleanup_metadata?.cleanup_reason) {
          const reason = item.cleanup_metadata.cleanup_reason;
          if (!acc[reason]) {
            acc[reason] = { count: 0, last_cleanup: item.cleanup_metadata.cleanup_timestamp };
          }
          acc[reason].count++;
          if (item.cleanup_metadata.cleanup_timestamp > acc[reason].last_cleanup) {
            acc[reason].last_cleanup = item.cleanup_metadata.cleanup_timestamp;
          }
        }
        return acc;
      }, {} as Record<string, { count: number; last_cleanup: string }>) || {};

      const recent_cleanup_activity = Object.entries(cleanupActivity).map(([reason, data]) => ({
        cleanup_reason: reason,
        count: data.count,
        last_cleanup: data.last_cleanup
      }));

      // Count auto-cleaned vs manually dismissed
      const auto_cleaned_count = cleanupData.data?.filter(item => 
        item.cleanup_metadata?.cleanup_reason && 
        !item.cleanup_metadata.cleanup_reason.includes('manual')
      ).length || 0;

      const manually_dismissed_count = cleanupData.data?.filter(item => 
        item.cleanup_metadata?.was_dismissed === true
      ).length || 0;

      return {
        active_count: activeData.count || 0,
        history_count: historyData.count || 0,
        unread_count: unreadData.count || 0,
        auto_cleaned_count,
        manually_dismissed_count,
        recent_cleanup_activity
      };
    } catch (error) {
      logger.error('Failed to get notification stats:', error);
      throw error;
    }
  }

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }
}

export const notificationService = NotificationService.getInstance(); 